import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { t } from "i18next";
import { type FC, useCallback, useState } from "react";
import SystemDialog from "../../../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { useFormatPersonName } from "../../../../hooks/useNameFormat";
import { STATUS } from "../../../../theme/tokens";
import type { NurseWithExternalData } from "../../../../utils/models";
import { CommonHeader } from "../util/constants";

type Props = {
	nurse?: NurseWithExternalData;
	onRemoveNurseClick: () => void;
	isLoading: boolean;
};

type DialogState = "remove" | "remove_confirm";

/**
 * "Remove from department" section. Per-department status changes
 * (On Leave / Suspended / Inactive) and reactivation live in the personal-info
 * `EmployeeStatusControl`; this card only carries the irreversible hard-remove.
 */
export const RemoveFromDepartment: FC<Props> = ({ nurse, onRemoveNurseClick, isLoading }) => {
	const formatPersonName = useFormatPersonName();
	// DEFAULT: the section renders before a new profile is saved; its warnings only open for an existing nurse.
	const nurseName = nurse ? formatPersonName(nurse) : "";
	const [dialogState, setDialogState] = useState<DialogState | undefined>(undefined);

	const resetDialogState = useCallback(() => {
		setDialogState(undefined);
	}, []);

	const handleRemoveFirstConfirm = useCallback(() => {
		setDialogState("remove_confirm");
	}, []);

	const handleRemoveConfirm = useCallback(() => {
		resetDialogState();
		onRemoveNurseClick();
	}, [onRemoveNurseClick, resetDialogState]);

	const dialogConfig =
		dialogState === "remove"
			? {
					title: t("remove.nurse.title"),
					body: (
						<div>
							{t("remove.nurse.warning", { name: nurseName })}
							<br />
							<br />
							<b>{t("remove.nurse.impact.header")}</b>
							<ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
								<li>{t("remove.nurse.impact.shifts")}</li>
								<li>{t("remove.nurse.impact.preferences")}</li>
								<li>{t("remove.nurse.impact.requests")}</li>
								<li>{t("remove.nurse.impact.recurring")}</li>
								<li>{t("remove.nurse.impact.external")}</li>
							</ul>
							<b style={{ color: STATUS.ERROR.main }}>{t("remove.nurse.irreversible")}</b>
						</div>
					),
					onConfirm: handleRemoveFirstConfirm,
					cancel: t("cancel"),
					confirm: t("remove.nurse.confirm.first"),
				}
			: dialogState === "remove_confirm"
				? {
						title: t("remove.nurse.final.title"),
						body: <div style={{ color: STATUS.ERROR.main, fontWeight: "bold" }}>{t("remove.nurse.final.warning", { name: nurseName })}</div>,
						onConfirm: handleRemoveConfirm,
						cancel: t("cancel"),
						confirm: t("remove.nurse.confirm.final"),
					}
				: undefined;

	return (
		<Box
			data-testid="profile-remove-from-department-section"
			sx={{
				width: "100%",
				display: "flex",
				flexDirection: "column",
				alignItems: "start",
				rowGap: "16px",
				justifyContent: "space-between",
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "start", width: "100%" }}>
				<CommonHeader>{t("remove.nurse")}</CommonHeader>
			</Box>
			<Box sx={{ display: "flex", flexDirection: "row", width: "100%", alignItems: "center", justifyContent: "start" }}>
				<SystemButton
					data-testid="profile-remove-nurse-button"
					key={"RemoveNurseButton"}
					buttonVariant={"secondary"}
					onClick={() => setDialogState("remove")}
					sx={{
						width: "fit-content",
						height: "fit-content",
						px: { xs: 0, md: 2 },
						py: { xs: 0, md: 1 },
						fontSize: { xs: "12px", md: "14px" },
						textWrap: { xs: "wrap", md: "nowrap" },
						color: STATUS.ERROR.main,
						borderColor: STATUS.ERROR.main,
						"&:hover": {
							borderColor: STATUS.ERROR.dark,
							backgroundColor: alpha(STATUS.ERROR.main, 0.04),
						},
					}}
					disabled={isLoading}
				>
					{t("remove.nurse.button")}
				</SystemButton>
			</Box>
			{dialogState && dialogConfig && (
				<SystemDialog
					open={!!dialogState}
					onClose={resetDialogState}
					title={dialogConfig.title}
					body={dialogConfig.body}
					onConfirm={dialogConfig.onConfirm}
					cancel={dialogConfig.cancel}
					confirm={dialogConfig.confirm}
				/>
			)}
		</Box>
	);
};
